import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { syncLanguageToProfile } from '../lib/languageSync';

/**
 * ★ 2026-08-14 (Fase 55) — LanguageSwitcher
 *
 * Selettore lingua globale (CRM header + landing pubblica).
 * Salva la scelta in `localStorage.gp_locale` (persiste su F5/logout).
 * Il primo accesso mostra un modal a parte per scegliere la lingua.
 *
 * Lingue supportate: Italiano (default), English, Español.
 * Norvegese e Russo sono presenti nei file locale ma non esposti in UI
 * (rimarranno disponibili per deploy multi-lingua dedicati via
 * REACT_APP_DEFAULT_LOCALE).
 */
export const SUPPORTED_LOCALES = [
  { code: 'it', label: 'Italiano', flag: '🇮🇹', native: 'Italiano' },
  { code: 'en', label: 'English',  flag: '🇬🇧', native: 'English'  },
  { code: 'es', label: 'Español',  flag: '🇪🇸', native: 'Español'  },
];

export default function LanguageSwitcher({ variant = 'default', className = '' }) {
  const { i18n, t } = useTranslation();
  const current = SUPPORTED_LOCALES.find((l) => l.code === i18n.language)
    || SUPPORTED_LOCALES.find((l) => i18n.language?.startsWith(l.code))
    || SUPPORTED_LOCALES[0];

  const handleChange = (code) => {
    i18n.changeLanguage(code);
    try {
      localStorage.setItem('gp_locale', code);
      localStorage.setItem('gp_locale_chosen', '1');
    } catch { /* ignore */ }
    // ★ 2026-08-26 (Fase 55) — Persist su profilo backend (se autenticato).
    syncLanguageToProfile(code);
  };

  const compact = variant === 'compact';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="language-switcher"
          aria-label={t('common.language', 'Lingua')}
          className={`inline-flex items-center gap-1.5 rounded-full border border-border bg-background hover:bg-[var(--surface-2)] transition-colors ${
            compact ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm'
          } ${className}`}
        >
          <Globe className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
          <span className={compact ? 'text-base' : 'text-lg leading-none'}>{current.flag}</span>
          {!compact && (
            <span className="hidden sm:inline font-medium">
              {current.code.toUpperCase()}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {t('common.language', 'Lingua')}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {SUPPORTED_LOCALES.map((l) => {
          const active = current.code === l.code;
          return (
            <DropdownMenuItem
              key={l.code}
              onSelect={() => handleChange(l.code)}
              data-testid={`language-option-${l.code}`}
              className="cursor-pointer flex items-center gap-2"
            >
              <span className="text-lg leading-none">{l.flag}</span>
              <span className="flex-1">
                <span className="font-medium">{l.native}</span>
                <span className="ml-2 text-[10px] uppercase text-muted-foreground">{l.code}</span>
              </span>
              {active && <Check className="w-4 h-4 text-[var(--gp-gold)]" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
